import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Collapse from '@mui/material/Collapse';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import Alert from 'shared/components/alert/alert';
import { useFeatureFlag } from 'shared/hooks/feature-flag';
import { FeatureFlag } from 'shared/models/feature-flag';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import styles from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-configuration.module.scss';

const GatewaySettingsConfiguration = (): React.JSX.Element => {
  const { t } = useTranslation('gateway');

  const {
    gatewayDetailsHook: { isTogglingSonevoApiDisabled, isTogglingMediaserverDisabled, currentSelectedGateway, viewingMode },
  } = useGatewaysContextProvider();

  const {
    control,
    formState: { dirtyFields },
    watch,
  } = useFormContext();

  const isViewing = viewingMode === 'viewing';
  const isEditing = viewingMode === 'editing';
  const { isEnabled: isIntercomMappingsEnabled } = useFeatureFlag(FeatureFlag.INTERCOM_MAPPINGS);

  const isAlertVisible =
    isEditing &&
    (dirtyFields.isSonevoApiEnabled ||
      dirtyFields.isAlarmmonitorEnabled ||
      dirtyFields.isAudioEnabled ||
      dirtyFields.isMediaserverEnabled ||
      dirtyFields.isKadexApiEnabled ||
      dirtyFields.isIS32ApiEnabled ||
      dirtyFields.isIPUnitApiEnabled ||
      dirtyFields.isDomipApiEnabled ||
      dirtyFields.isWanderingPreventionApiEnabled ||
      dirtyFields.isIntercomModuleEnabled);

  const watchSonevoApiEnabled = watch('isSonevoApiEnabled');

  return (
    <div className={styles.container}>
      <Collapse in={isAlertVisible}>
        <div className={styles.explanationRow}>
          <Alert body={t('details.settings.configurationRemark')} />
        </div>
      </Collapse>
      <div className={styles.configurationRow}>
        <Typography variant="subtitle1">{t('details.information.automaticDeviceCreation')}</Typography>
        <Controller
          name="shouldCreateDevicesAutomatically"
          control={control}
          render={({ field }) => <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing} />}
        />
      </div>
      <Tooltip
        title={isEditing && isTogglingSonevoApiDisabled(currentSelectedGateway) ? t('details.settings.configurationDisabledTooltip') : ''}
        followCursor
        slots={{
          transition: Zoom,
        }}
      >
        <div className={styles.configurationRow}>
          <Typography variant="subtitle1">{t('details.settings.configuration.sonevoApi')}</Typography>
          <Controller
            name="isSonevoApiEnabled"
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                type="checkbox"
                checked={field.value}
                disabled={isViewing || (isEditing && isTogglingSonevoApiDisabled(currentSelectedGateway))}
              />
            )}
          />
        </div>
      </Tooltip>
      <div className={styles.configurationRow}>
        <Typography variant="subtitle1">{t('details.settings.configuration.alarmMonitor')}</Typography>
        <Controller
          name="isAlarmmonitorEnabled"
          control={control}
          render={({ field }) => <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing} />}
        />
      </div>
      <div className={styles.configurationRow}>
        <Typography variant="subtitle1">{t('details.settings.configuration.audioModule')}</Typography>
        <Controller
          name="isAudioEnabled"
          control={control}
          render={({ field }) => <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing} />}
        />
      </div>
      <Tooltip
        title={isEditing && isTogglingMediaserverDisabled(currentSelectedGateway) ? t('details.settings.configurationDisabledTooltip') : ''}
        followCursor
        slots={{
          transition: Zoom,
        }}
      >
        <div className={styles.configurationRow}>
          <Typography variant="subtitle1">{t('details.settings.configuration.mediaserverModule')}</Typography>
          <Controller
            name="isMediaserverEnabled"
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                type="checkbox"
                checked={field.value}
                disabled={isViewing || (isEditing && isTogglingMediaserverDisabled(currentSelectedGateway))}
              />
            )}
          />
        </div>
      </Tooltip>
      <div className={styles.configurationRow}>
        <Typography variant="subtitle1">{t('details.settings.configuration.kadexApi')}</Typography>
        <Controller
          name="isKadexApiEnabled"
          control={control}
          render={({ field }) => <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing} />}
        />
      </div>
      <Tooltip
        title={isEditing && !watchSonevoApiEnabled ? t('details.settings.configurationDisabledTooltipSonevoApiDisabled') : ''}
        followCursor
        slots={{
          transition: Zoom,
        }}
      >
        <div className={styles.configurationRow}>
          <Typography variant="subtitle1">{t('details.settings.configuration.is32Api')}</Typography>
          <Controller
            name="isIS32ApiEnabled"
            control={control}
            render={({ field }) => (
              <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing || !watchSonevoApiEnabled} />
            )}
          />
        </div>
      </Tooltip>
      <Tooltip
        title={isEditing && !watchSonevoApiEnabled ? t('details.settings.configurationDisabledTooltipSonevoApiDisabled') : ''}
        followCursor
        slots={{
          transition: Zoom,
        }}
      >
        <div className={styles.configurationRow}>
          <Typography variant="subtitle1">{t('details.settings.configuration.ipUnitApi')}</Typography>
          <Controller
            name="isIPUnitApiEnabled"
            control={control}
            render={({ field }) => (
              <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing || !watchSonevoApiEnabled} />
            )}
          />
        </div>
      </Tooltip>
      <Tooltip
        title={isEditing && !watchSonevoApiEnabled ? t('details.settings.configurationDisabledTooltipSonevoApiDisabled') : ''}
        followCursor
        slots={{
          transition: Zoom,
        }}
      >
        <div className={styles.configurationRow}>
          <Typography variant="subtitle1">{t('details.settings.configuration.domipApi')}</Typography>
          <Controller
            name="isDomipApiEnabled"
            control={control}
            render={({ field }) => (
              <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing || !watchSonevoApiEnabled} />
            )}
          />
        </div>
      </Tooltip>
      <Tooltip
        title={isEditing && !watchSonevoApiEnabled ? t('details.settings.configurationDisabledTooltipSonevoApiDisabled') : ''}
        followCursor
        slots={{
          transition: Zoom,
        }}
      >
        <div className={styles.configurationRow}>
          <Typography variant="subtitle1">{t('details.settings.configuration.wanderingPreventionModule')}</Typography>
          <Controller
            name="isWanderingPreventionApiEnabled"
            control={control}
            render={({ field }) => (
              <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing || !watchSonevoApiEnabled} />
            )}
          />
        </div>
      </Tooltip>
      {isIntercomMappingsEnabled && (
        <Tooltip
          title={isEditing && !watchSonevoApiEnabled ? t('details.settings.configurationDisabledTooltipSonevoApiDisabled') : ''}
          followCursor
          slots={{
            transition: Zoom,
          }}
        >
          <div className={styles.configurationRow}>
            <Typography variant="subtitle1">{t('details.settings.configuration.intercomModule')}</Typography>
            <Controller
              name="isIntercomModuleEnabled"
              control={control}
              render={({ field }) => (
                <Switch {...field} type="checkbox" checked={field.value} disabled={isViewing || !watchSonevoApiEnabled} />
              )}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default GatewaySettingsConfiguration;
